/* eslint-disable react-hooks/exhaustive-deps */
import { has } from '@/commonFunctions/utils'
import { Button, ButtonPrimary } from '@/components/button'
import { PlayerInfoSection } from '@/components/common'
import { MainContent } from '@/components/content'
import { ArrowBack, People, Person, RemoveCircle, UserAddIcon, UserIconFilled } from '@/components/icons'
import { MainLayout } from '@/components/layouts'
import { InvitePlayerModal } from '@/components/modal'
import { Search } from '@/components/search'
import { MainSidebar, MobileSidebar } from '@/components/sidebar'
import { PlayerItemLoading } from '@/components/skeleton'
import {
  CLUB_MODIFY,
  LIMIT,
  OFFSET,
  SCROLLING_HEIGHT_ABS_ERROR,
  SCROLLING_THROTTLING_DURATION,
} from '@/constants/Common'
import { ROUTE_CLUBS } from '@/constants/Routes'
import { useDebounce } from '@/hooks/useDebounce'
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux'
import { appSelector } from '@/pages/home/selectors'
import { userSelector } from '@/pages/user/selectors'
import { useAddMembers, useAssignOrganizer, useSearchPlayerAddToClub } from '@/services'
import { Player } from '@/types'
import { throttle } from 'lodash'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { getClubRoles } from '../clubSlice'
import { clubSelector } from '../selectors'

type Props = {
  isAddOrganizer?: boolean
}

export const InvitePlayer = ({ isAddOrganizer }: Props) => {
  const { t } = useTranslation()

  const navigate = useNavigate()
  const params = useParams()
  const dispatch = useAppDispatch()
  const { user } = useAppSelector(userSelector)
  const { isShowDropdown } = useAppSelector(appSelector)
  const { permissions } = useAppSelector(clubSelector)

  const { clubId } = params

  const { mutate: addMembers } = useAddMembers()
  const { mutate: assignOrganizer } = useAssignOrganizer()

  const [searchText, setMemberSearchText] = useState('')
  const [offset, setOffset] = useState(OFFSET)
  const [players, setPlayers] = useState<Map<number, Player>>(new Map())
  const [selectedMembers, setSelectedMembers] = useState<Player[]>([])
  const [showInvitePlayerModal, setShowInvitePlayerModal] = useState(false)

  const debouncedSearchText = useDebounce<string>(searchText || '*', 200)

  const { data: resSearchPlayer, isLoading } = useSearchPlayerAddToClub({
    clubId: clubId,
    limit: LIMIT,
    offset: offset,
    query: debouncedSearchText ? debouncedSearchText : '*',
    includeUnclaimedPlayers: true,
    filter: isAddOrganizer
      ? {}
      : {
          lat: user?.addresses?.[0]?.latitude,
          lng: user?.addresses?.[0]?.longitude,
        },
    excludeClubMembers: {
      clubId: clubId || '',
      roles: isAddOrganizer ? ['ORGANIZER'] : [],
    },
  })

  const onChangeSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMemberSearchText(e.target.value)
    setOffset(OFFSET)
  }

  const toggleInvitePlayer = () => setShowInvitePlayerModal(!showInvitePlayerModal)

  const handleBackToClubDetail = () => {
    navigate(`${ROUTE_CLUBS}/${clubId}`)
  }

  const handleBackToClubEdit = () => {
    navigate(`${ROUTE_CLUBS}/${clubId}/edit`)
  }

  const handleScroll = throttle(() => {
    if (
      Math.abs(window.innerHeight + document.documentElement.scrollTop - document.documentElement.offsetHeight) >
      SCROLLING_HEIGHT_ABS_ERROR
    ) {
      return
    }

    setOffset((pre) => pre + LIMIT)
  }, SCROLLING_THROTTLING_DURATION)

  const onChangeSelect = (item: Player) => {
    if (isAddOrganizer) {
      setSelectedMembers([item])
    } else {
      const find = selectedMembers.find((member) => member.id === item.id)
      if (find) {
        const filterArr = [...selectedMembers].filter((member) => member.id !== item.id)
        setSelectedMembers(filterArr)
      } else {
        setSelectedMembers([...selectedMembers, item])
      }
    }
  }

  const onRemoveSelectedMember = (id: number) => {
    const filterArr = [...selectedMembers].filter((item) => item.id !== id)
    setSelectedMembers(filterArr)
  }

  const onAddMembers = () => {
    if (clubId && isAddOrganizer && selectedMembers.length > 0) {
      const member = selectedMembers[0]
      assignOrganizer(
        {
          clubId: Number(clubId),
          userId: member.id,
          roleId: 7338457602,
        },
        {
          onSuccess: () => {
            toast.success('Added successfully')
            handleBackToClubEdit()
          },
          onError: (error: any) => {
            toast.error(error?.response?.data?.message || 'An error occurred while adding the organizer')
          },
        }
      )
    } else {
      const userIds = selectedMembers.map((item) => item.id.toString())
      if (clubId && userIds.length) {
        addMembers(
          { clubId: Number(clubId), userIds: userIds },
          {
            onSuccess: () => {
              handleBackToClubDetail()
            },
          }
        )
      }
    }
  }

  useEffect(() => {
    if (resSearchPlayer)
      setPlayers((old) => {
        let temp: Map<number, Player>
        if (offset === OFFSET) {
          temp = new Map()
        } else {
          temp = old
        }

        resSearchPlayer.result.hits.forEach((p) => {
          temp.set(p.id, p)
        })

        return temp
      })
    if (resSearchPlayer?.result.hasMore) window.addEventListener('scroll', handleScroll)

    return () => window.removeEventListener('scroll', handleScroll)
  }, [resSearchPlayer])

  useEffect(() => {
    if (clubId && user?.id) {
      dispatch(getClubRoles({ clubId: Number(clubId), userId: user?.id }))
    }
  }, [clubId, user])

  useEffect(() => {
    if (permissions && !has(permissions, CLUB_MODIFY))
      navigate(`${ROUTE_CLUBS}/${clubId}`, { relative: 'path', replace: true })
  }, [permissions])

  return (
    <>
      {!isShowDropdown && <MobileSidebar />}
      <MainLayout>
        <MainSidebar />
        <MainContent>
          <div className="flex flex-col gap-y-4 mb-20 lg:mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between pt-4 pb-3">
              <div className="flex gap-x-3 items-start">
                <Button onClick={handleBackToClubDetail} className="hover:opacity-50">
                  <ArrowBack className="text-white" />
                </Button>
                <div className="flex flex-col gap-y-2">
                  <h1 className="text-sm md:text-xl text-white font-semibold">
                    {isAddOrganizer ? t('club.addOrganizers') : t('club.invitePlayers')}
                  </h1>
                  <p className="text-sm text-white">
                    {isAddOrganizer ? t('club.clubOrganizers') : t('club.clubPlayers')}
                  </p>
                </div>
              </div>

              <ButtonPrimary
                title={isAddOrganizer ? t('club.addOrganizer') : t('club.addMember')}
                className="py-3 px-5 mt-3 sm:mt-0"
                onClick={onAddMembers}
              />
            </div>

            <div className="flex items-center gap-x-4">
              <Search searchText={searchText} onChangeInput={onChangeSearchInput} className="py-2 px-3 w-56 gap-x-2" />
              {!isAddOrganizer && (
                <button
                  className="flex items-center bg-primary-500 py-2 px-3 rounded-full text-sm text-white font-semibold gap-x-2 hover:bg-opacity-70"
                  onClick={toggleInvitePlayer}
                >
                  <UserAddIcon className="w-4 h-4 text-white" />
                  {t('common.invite')}
                </button>
              )}
            </div>

            <div className="flex gap-x-4">
              {!isAddOrganizer &&
                selectedMembers?.map((item) => (
                  <div className="flex flex-col gap-y-2 w-32 items-center relative" key={item.id}>
                    {item.imageUrl ? (
                      <img src={item.imageUrl} className="rounded-full w-16 h-16" alt="avatar" />
                    ) : (
                      <UserIconFilled className="w-16 h-16 text-primary-500" />
                    )}
                      <span className="text-sm text-white font-semibold">{item?.fullName}</span>

                    <Button className="hover:opacity-50" onClick={() => onRemoveSelectedMember(item.id)}>
                      <RemoveCircle className="absolute top-0 right-0" />
                    </Button>
                  </div>
                ))}
            </div>

            <div className="flex flex-col gap-y-4">
              {Array.from(players.values()).map((item) => {
                const find = selectedMembers.find((member) => member.id === item.id)
                return (
                  <div className="flex justify-between bg-white rounded-2xl p-4" key={item.id}>
                    <div className="flex items-center gap-x-3 cursor-pointer" onClick={() => onChangeSelect(item)}>
                      {/* eslint-disable-next-line */}
                      <input
                        type={isAddOrganizer ? 'radio' : 'checkbox'}
                        className="w-5 h-5 cursor-pointer"
                        checked={!!find}
                        onChange={() => {}}
                      />
                      <PlayerInfoSection player={item} />
                    </div>
                    <div className="flex gap-x-1 items-center">
                      <div className="bg-navy-900 bg-opacity-5 rounded-lg px-2 w-11 h-9  flex flex-col justify-center items-center">
                        <People className="text-navy-900" />
                        <span className="text-xs text-navy-900 font-medium">{item?.ratings?.doubles}</span>
                      </div>
                      <div className="bg-navy-900 bg-opacity-5 rounded-lg px-2 w-10 h-9  flex flex-col justify-center items-center">
                        <Person className="text-navy-900" />
                        <span className="text-xs text-navy-900 font-medium">{item?.ratings?.singles}</span>
                      </div>
                    </div>
                  </div>
                )
              })}
              {isLoading && (
                <>
                  <PlayerItemLoading className="!max-w-[964px]" />
                  <PlayerItemLoading className="!max-w-[964px]" />
                  <PlayerItemLoading className="!max-w-[964px]" />
                  <PlayerItemLoading className="!max-w-[964px]" />
                  <PlayerItemLoading className="!max-w-[964px]" />
                  <PlayerItemLoading className="!max-w-[964px]" />
                  <PlayerItemLoading className="!max-w-[964px]" />
                  <PlayerItemLoading className="!max-w-[964px]" />
                </>
              )}

              {resSearchPlayer && !resSearchPlayer?.result?.hasMore && (
                <div className="flex justify-center">
                  <p className="text-slate-300 text-base font-semibold">{t('common.noMoreResults')}</p>
                </div>
              )}
            </div>
          </div>
        </MainContent>
      </MainLayout>
      {clubId && <InvitePlayerModal open={showInvitePlayerModal} onClose={toggleInvitePlayer} clubId={clubId} />}
    </>
  )
}
